<template>
  <Modal
    v-model:visible="isVisible"
    class="event-modal"
    :header="header"
    size="md"
    @keypress.enter="onSave"
    @keypress.esc="onCancel"
  >
    <div class="flex flex-gap-1">
      <div class="w-6">
        <InputWrapper
          v-if="!event.id"
          v-slot="{ ariaProps, inputClasses, inputId, invalid }"
          input-id="event-type"
          class="field"
          :label="t('common.type')"
          :errors="v$.eventType.$errors"
        >
          <RygenDropdown
            :id="inputId"
            v-model="v$.eventType.$model"
            v-bind="ariaProps"
            :class="inputClasses"
            :invalid="invalid"
            :options="eventTypeOptions"
            option-label="label"
            option-value="value"
            filter
            :placeholder="t('common.type')"
            @update:model-value="onTypeChange"
          />
        </InputWrapper>

        <InputWrapper
          v-if="!event.isMilestone"
          v-slot="{ ariaProps, inputClasses, inputId, invalid }"
          input-id="event-severity"
          class="field"
          :label="t('events.severity')"
          :errors="v$.severity.$errors"
        >
          <RygenDropdown
            :id="inputId"
            v-model="v$.severity.$model"
            v-bind="ariaProps"
            :class="inputClasses"
            :invalid="invalid"
            :options="severityOptions"
            option-label="label"
            option-value="value"
            filter
            :placeholder="t('events.severity')"
          />
        </InputWrapper>

        <InputWrapper
          v-if="!!(event instanceof ShipmentMilestoneEvent)"
          v-slot="{ ariaProps, inputClasses, inputId, invalid }"
          input-id="actual-arrival"
          class="field"
          :label="t('shipments.dates.actualArrival')"
          :errors="v$.actualArrival.$errors"
          :warning="actualArrivalWarning"
          :help-text="actualArrivalHelpText"
        >
          <div class="flex flex-gap-1 event-calendar-tz-wrapper">
            <RygenCalendar
              :id="inputId"
              v-model="event.actualArrival"
              v-bind="ariaProps"
              :class="inputClasses"
              :invalid="invalid"
              :disabled="areActualDatesDisabled"
              :time-zone="event.actualLocation.timeZone"
              class="flex-grow-1"
              date-format="mm/dd/yy"
              show-icon
              show-time
              hour-format="12"
              :step-minute="15"
              :pt="{
                panel: {
                  'data-testid': 'shipment-event-modal-actual-arrival-panel',
                },
              }"
              :placeholder="t('shipments.dates.actualArrival')"
            />
            <TimeZoneDropdown
              v-model="event.actualLocation.timeZone"
              :date-time-basis="event.actualArrival"
              :disabled="!!event.relatedStopId"
              :placeholder="t('common.timezones')"
            />
          </div>
        </InputWrapper>
        <InputWrapper
          v-else
          v-slot="{ ariaProps, inputClasses, inputId, invalid }"
          input-id="actual-date"
          class="field"
          :label="t('shipments.dates.actualDateTime')"
          :errors="v$.actualDate.$errors"
        >
          <div class="flex flex-gap-1 event-calendar-tz-wrapper">
            <RygenCalendar
              :id="inputId"
              v-model="event.actualDate"
              v-bind="ariaProps"
              :class="inputClasses"
              :invalid="invalid"
              :disabled="areActualDatesDisabled"
              :time-zone="event.actualLocation.timeZone"
              class="flex-grow-1"
              date-format="mm/dd/yy"
              show-icon
              show-time
              hour-format="12"
              :step-minute="15"
              :pt="{
                panel: {
                  'data-testid': 'shipment-event-modal-actual-date-panel',
                },
              }"
              :placeholder="t('shipments.dates.actualDateTime')"
            />
            <TimeZoneDropdown
              v-model="event.actualLocation.timeZone"
              :date-time-basis="event.actualDate"
              :disabled="!!event.relatedStopId"
              :placeholder="t('common.timezones')"
            />
          </div>
        </InputWrapper>

        <InputWrapper
          v-if="!!(event instanceof ShipmentMilestoneEvent)"
          v-slot="{ ariaProps, inputClasses, inputId, invalid }"
          input-id="actual-departure"
          class="field"
          :label="t('shipments.dates.actualDeparture')"
          :errors="v$.actualDeparture.$errors"
          :warning="actualDepartureWarning"
          :help-text="actualDepartureHelpText"
        >
          <div class="flex flex-gap-1 event-calendar-tz-wrapper">
            <RygenCalendar
              :id="inputId"
              v-model="event.actualDeparture"
              v-bind="ariaProps"
              :class="inputClasses"
              :invalid="invalid"
              :disabled="areActualDatesDisabled"
              :time-zone="event.actualLocation.timeZone"
              class="flex-grow-1"
              date-format="mm/dd/yy"
              show-icon
              show-time
              hour-format="12"
              :step-minute="15"
              :pt="{
                panel: {
                  'data-testid': 'shipment-event-modal-actual-departure-panel',
                },
              }"
              :placeholder="t('shipments.dates.actualDeparture')"
            />
            <TimeZoneDropdown
              v-model="event.actualLocation.timeZone"
              :date-time-basis="event.actualDeparture"
              :disabled="!!event.relatedStopId"
              :placeholder="t('common.timezones')"
            />
          </div>
        </InputWrapper>

        <InputWrapper
          v-slot="{ ariaProps, inputId }"
          input-id="event-comment"
          class="field"
          :label="t('common.comments')"
          optional
        >
          <Textarea
            :id="inputId"
            v-model="event.comment"
            v-bind="ariaProps"
            rows="5"
            cols="30"
            :placeholder="t('common.comments')"
            @keypress.enter="$event.stopPropagation()"
            @keypress.shift.enter="onSave"
          />
        </InputWrapper>

        <InputWrapper
          v-if="showSource"
          class="field"
          :label="t('events.source')"
          input-id="event-source"
        >
          {{
            event.source
              ? translateEnum(event.source, 'ShipmentEventSource')
              : '-'
          }}
        </InputWrapper>
      </div>

      <div class="w-6">
        <ShipmentEventModalStopSelector
          v-model="event"
          :shipment-stops="shipmentStops"
        />

        <InputWrapper
          v-slot="{ ariaProps, inputId }"
          input-id="event-city"
          class="field"
          :label="t('address.city')"
          optional
        >
          <InputText
            :id="inputId"
            v-model="event.actualLocation.city"
            v-bind="ariaProps"
            :disabled="event.isMilestone || !!event.relatedStopId"
            :placeholder="t('address.city')"
          />
        </InputWrapper>

        <InputWrapper
          v-slot="{ ariaProps, inputId }"
          input-id="event-state"
          class="field"
          :label="t('address.state')"
          optional
        >
          <InputRegion
            :id="inputId"
            v-model="event.actualLocation.state"
            v-bind="ariaProps"
            :country="event.actualLocation.country"
            :disabled="event.isMilestone || !!event.relatedStopId"
            append-to="body"
          />
        </InputWrapper>

        <InputWrapper
          v-slot="{ ariaProps, inputId }"
          input-id="event-postal-code"
          class="field"
          :label="t('address.postalCode')"
          optional
        >
          <InputText
            :id="inputId"
            v-model="event.actualLocation.postalCode"
            v-bind="ariaProps"
            :disabled="event.isMilestone || !!event.relatedStopId"
            :placeholder="t('address.postalCode')"
          />
        </InputWrapper>
      </div>
    </div>

    <template #footer>
      <RygenButton
        mode="tertiary"
        :label="t('common.cancel')"
        @click="onCancel"
      />
      <RygenButton
        mode="primary"
        icon="pi pi-save"
        :disabled="disabled"
        :label="t('common.saveParam', { param: t('events.events') })"
        @click="onSave"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { ValidationRule } from '@vuelidate/core';
import isEqual from 'lodash/isEqual';
import { computed, PropType, ref, Ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  afterDateTime,
  beforeDateTime,
  required,
  useUid,
  useValidations,
} from '@rygen/rygen-ui/composables';
import { renderDateTime, translateEnum } from '@rygen/rygen-ui/i18n';
import { DropdownOption } from '@rygen/rygen-ui/model';

import {
  isEditableShipmentStatus,
  isPickupOrDropoffType,
  isShipmentDeliveryEvent,
  isShipmentOriginEvent,
  Shipment,
  ShipmentEvent,
  ShipmentEventLocation,
  ShipmentEventSeverity,
  ShipmentEventType,
  ShipmentMilestoneEvent,
  ShipmentStop,
} from '@/model';
import { buildShipmentEvent } from '@/util';

import ShipmentEventModalStopSelector from '@/components/shipment/ShipmentEventModalStopSelector.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import InputRegion from '@rygen/rygen-ui/components/form/InputRegion.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenCalendar from '@rygen/rygen-ui/components/form/RygenCalendar.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';
import TimeZoneDropdown from '@rygen/rygen-ui/components/form/TimeZoneDropdown.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  shipmentEvent: {
    type: Object as PropType<ShipmentEvent>,
    default: () => new ShipmentEvent(),
  },
  eventTypeOptions: {
    type: Array as PropType<DropdownOption<string>[]>,
    default: () => [],
  },
  severityOptions: {
    type: Array as PropType<DropdownOption<string>[]>,
    default: () => [],
  },
  shipmentStops: {
    type: Array as PropType<ShipmentStop[]>,
    default: () => [],
  },
  shipment: {
    type: Object as PropType<Shipment>,
    default: () => new Shipment(),
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
  showSource: {
    type: Boolean,
    default: () => true,
  },
  clientRequiresBothDates: {
    type: Boolean,
    default: () => false,
  },
});
const {
  shipmentEvent,
  eventTypeOptions,
  severityOptions,
  shipmentStops,
  shipment,
  disabled,
  showSource,
  clientRequiresBothDates,
} = toRefs(props);
const emit = defineEmits<{
  (event: 'cancel'): void;
  (event: 'save', shipmentEvent: ShipmentEvent): void;
}>();

const { t } = useI18n();
const { uid } = useUid('shipment-event-modal');

const event: Ref<ShipmentEvent> = ref(buildShipmentEvent(shipmentEvent.value));
const rules = computed(() => {
  const r: Record<string, Record<string, ValidationRule>> = {
    eventType: {
      required: required(t('common.type')),
    },
    severity: {
      required: required(t('events.severity')),
    },
    // These are here just to create the references on v$. Don't put any rules
    // here; put them below in one of the if/else blocks.
    actualDate: {},
    actualArrival: {},
    actualDeparture: {},
  };

  if (event.value.isMilestone) {
    r.actualArrival = {
      before: beforeDateTime(
        () => (event.value as ShipmentMilestoneEvent).actualDeparture,
        t('shipments.dates.actualArrival'),
        t('shipments.dates.actualDeparture'),
        { strict: false },
      ),
    };
    r.actualDeparture = {
      after: afterDateTime(
        () => (event.value as ShipmentMilestoneEvent).actualArrival,
        t('shipments.dates.actualDeparture'),
        t('shipments.dates.actualArrival'),
        { strict: false },
      ),
    };
  } else {
    r.actualDate = {
      required: required(t('shipments.dates.actualDateTime')),
    };
    const stopId = event.value.relatedStopId ?? event.value.relatedStop?.id;
    const stop = shipmentStops.value.find((s) => s.id === stopId);
    const eventType = event.value.eventType;

    if (
      eventType === ShipmentEventType.PICK_UP_ARRIVE ||
      eventType === ShipmentEventType.DROPOFF_ARRIVE
    ) {
      r.actualDate.before = beforeDateTime(
        () => stop?.actualDeparture,
        t('shipments.dates.actualArrival'),
        t('shipments.dates.actualDeparture'),
        { strict: false },
      );
    }

    if (
      eventType === ShipmentEventType.PICK_UP_DEPART ||
      eventType === ShipmentEventType.DROPOFF_DEPART
    ) {
      r.actualDate.after = afterDateTime(
        () => stop?.actualArrival,
        t('shipments.dates.actualDeparture'),
        t('shipments.dates.actualArrival'),
        { strict: false },
      );
    }
  }

  return r;
});
const { v$, validate } = useValidations(event, rules, uid);

const actualArrivalWarning = ref('');
const actualDepartureWarning = ref('');

const header = computed(() => {
  if (!event.value.id) {
    return t('common.newParam', { param: t('events.events') });
  }

  let title = t('common.editParam', {
    param: t('events.events'),
  });

  if (event.value.eventType) {
    const eventType = translateEnum(event.value.eventType, 'ShipmentEventType');
    title += ` - ${eventType}`;
  }

  return title;
});

const areActualDatesDisabled = computed(() => {
  const isEditable = isEditableShipmentStatus(shipment.value.status);
  return isPickupOrDropoffType(event.value.eventType) && !isEditable;
});

const actualArrivalHelpText = computed(() => {
  if (!event.value.expectedDateRange?.start) {
    return;
  }

  const expectedArrival = renderDateTime(event.value.expectedDateRange.start, {
    timeZone: event.value.actualLocation?.timeZone,
  });
  const expectedArrivalLabel = t('shipments.dates.expectedArrival');
  return `${expectedArrivalLabel}: ${expectedArrival}`;
});

const actualDepartureHelpText = computed(() => {
  if (!event.value.expectedDateRange?.end) {
    return;
  }

  const expectedDeparture = renderDateTime(event.value.expectedDateRange.end, {
    timeZone: event.value.actualLocation?.timeZone,
  });
  const expectedDepartureLabel = t('shipments.dates.expectedDeparture');
  return `${expectedDepartureLabel}: ${expectedDeparture}`;
});

const validateDates = async (milestone: ShipmentMilestoneEvent) => {
  if (!milestone.actualDeparture) {
    actualDepartureWarning.value = clientRequiresBothDates.value
      ? t('events.warnings.missingBothDates')
      : t('events.warnings.missingDepartureDate');
  } else {
    actualDepartureWarning.value = '';
  }

  actualArrivalWarning.value =
    !milestone.actualArrival && clientRequiresBothDates.value
      ? t('events.warnings.missingBothDates')
      : '';
};

const onSave = async () => {
  if (await validate()) {
    emit('save', event.value);
    isVisible.value = false;
  }
};

const onCancel = () => {
  emit('cancel');
  isVisible.value = false;
};

const reset = () => {
  v$.value.$reset();
  actualArrivalWarning.value = '';
  actualDepartureWarning.value = '';
};

const onTypeChange = (newEventType: ShipmentEventType) => {
  if (isShipmentOriginEvent(newEventType)) {
    event.value.relatedStopId = shipmentStops.value.find(
      (stop) => stop.isPickup,
    )?.id;
  } else if (isShipmentDeliveryEvent(newEventType)) {
    event.value.relatedStopId = shipmentStops.value.find(
      (stop) => stop.isDropOff,
    )?.id;
  }
};

watch(
  isVisible,
  (newVal: boolean) => {
    if (!newVal) {
      event.value = new ShipmentEvent();
      return;
    }

    event.value = buildShipmentEvent(shipmentEvent.value);
    if (
      event.value instanceof ShipmentMilestoneEvent &&
      !!event.value.actualArrival
    ) {
      const relatedStop = shipmentStops.value.find(
        (s) => s.id === event.value.relatedStopId,
      );
      event.value.actualArrival = relatedStop?.actualArrival;
    }

    reset();
  },
  { immediate: true },
);

watch(
  () => ({ id: event.value.id, relatedStopId: event.value.relatedStopId }),
  (newEvent?: any, oldEvent?: any) => {
    const relatedStopIdUpdated =
      newEvent?.id === oldEvent?.id &&
      newEvent?.relatedStopId !== oldEvent?.relatedStopId;

    if (!event.value.severity) {
      event.value.severity = ShipmentEventSeverity.NORMAL;
    }

    if (relatedStopIdUpdated) {
      const newRelatedStopId = newEvent?.relatedStopId;
      const oldRelatedStopId = oldEvent?.relatedStopId;

      const newRelatedStop = shipmentStops.value.find(
        (stop) => stop.id === newRelatedStopId,
      );
      const oldRelatedStop = shipmentStops.value.find(
        (stop) => stop.id === oldRelatedStopId,
      );

      const actualLocation =
        event.value.actualLocation ?? new ShipmentEventLocation();
      const oldActualLocationIsEmpty =
        !actualLocation.city &&
        !actualLocation.state &&
        !actualLocation.postalCode;
      const oldActualLocationIsOldStop =
        !oldActualLocationIsEmpty &&
        ((oldRelatedStop?.location?.city === actualLocation.city &&
          oldRelatedStop?.location?.state === actualLocation.state) ||
          oldRelatedStop?.location?.postalCode === actualLocation.postalCode);
      const shouldUpdateLocation =
        oldActualLocationIsEmpty || oldActualLocationIsOldStop;
      if (shouldUpdateLocation) {
        event.value.actualLocation = new ShipmentEventLocation({
          ...newRelatedStop?.location,
        });
      }
    }
  },
  { deep: true },
);

watch(
  () => {
    return event.value instanceof ShipmentMilestoneEvent
      ? {
          actualArrival: event.value.actualArrival,
          actualDeparture: event.value.actualDeparture,
        }
      : null;
  },
  (newVal: any | null, oldVal: any | null) => {
    if (newVal == null || oldVal == null) {
      return;
    }

    const hasArrivalChanged = !isEqual(
      newVal.actualArrival,
      oldVal.actualArrival,
    );
    const hasDepartureChanged = !isEqual(
      newVal.actualDeparture,
      oldVal.actualDeparture,
    );
    if (hasArrivalChanged || hasDepartureChanged) {
      validateDates(event.value as ShipmentMilestoneEvent);
    }
  },
  { deep: true },
);
</script>
